const getRows = (payload, collectionKey) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.[collectionKey])) return payload[collectionKey];
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const normalizeAccounts = (payload) =>
  getRows(payload, "accounts")
    .map((account) => ({
      ...account,
      id: String(account?.number ?? account?.id ?? "").trim(),
      description: String(account?.description ?? account?.number ?? "").trim(),
    }))
    .filter((account) => account.id || account.description);

const normalizeProjects = (payload) =>
  getRows(payload, "projects")
    .map((project) => ({
      ...project,
      id: String(project?.number ?? project?.rawNumber ?? project?.id ?? "").trim(),
      description: String(project?.description ?? project?.number ?? "").trim(),
    }))
    .filter((project) => project.id || project.description);

const getJson = async (url) => {
  let response;
  try {
    response = await fetch(url, { method: "GET" });
  } catch (error) {
    throw new Error(`Could not connect to Acomba SDK at ${url}: ${error.message}`);
  }

  if (!response.ok) {
    throw new Error(`Acomba SDK request failed (${response.status}): ${url}`);
  }

  const responseText = await response.text();
  return responseText ? JSON.parse(responseText) : [];
};

const getAcombaServiceUrl = ({ ip, port }) => {
  const host = String(ip || "").trim();
  const sdkPort = String(port || "").trim();

  if (!host || !sdkPort) {
    throw new Error("Acomba SDK IP or port is missing.");
  }

  const hasProtocol =
    host.toLowerCase().startsWith("http://") || host.toLowerCase().startsWith("https://");
  let baseUrl = hasProtocol ? host : `http://${host}`;
  while (baseUrl.endsWith("/")) baseUrl = baseUrl.slice(0, -1);

  return `${baseUrl}:${sdkPort}/api/acomba`;
};

export const testAcombaSdk = async ({ ip, port, endpoint }) => {
  const url = `${getAcombaServiceUrl({ ip, port })}/${endpoint}`;
  let response;
  try {
    response = await fetch(url, { method: "GET" });
  } catch (error) {
    throw new Error(`Could not connect to Acomba SDK at ${url}: ${error.message}`);
  }
  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(responseText || `Acomba SDK request failed (${response.status}): ${url}`);
  }
  return responseText;
};

export const fetchAcombaSdkLists = async ({ ip, port }) => {
  const serviceUrl = getAcombaServiceUrl({ ip, port });
  const [accountsPayload, projectsPayload] = await Promise.all([
    getJson(`${serviceUrl}/accounts`),
    getJson(`${serviceUrl}/projects`),
  ]);

  return {
    accounts: normalizeAccounts(accountsPayload),
    projects: normalizeProjects(projectsPayload),
  };
};
export const postAcombaJournalEntry = async ({ ip, port, payload }) => {
  const host = String(ip || "").trim();
  const sdkPort = String(port || "").trim();

  if (!host || !sdkPort) {
    throw new Error("Acomba SDK IP or port is missing.");
  }

  const hasProtocol =
    host.toLowerCase().startsWith("http://") || host.toLowerCase().startsWith("https://");
  let baseUrl = hasProtocol ? host : `http://${host}`;
  while (baseUrl.endsWith("/")) baseUrl = baseUrl.slice(0, -1);

  const url = `${baseUrl}:${sdkPort}/api/acomba/journal-entry`;
  let response;

  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    throw new Error(`Could not connect to Acomba SDK at ${url}: ${error.message}`);
  }

  const responseText = await response.text();
  let responseData = responseText;

  if (responseText) {
    try {
      responseData = JSON.parse(responseText);
    } catch (error) {
      // Keep a non-JSON response available for display.
    }
  }

  if (!response.ok) {
    const message =
      responseData?.message || responseText || `Acomba SDK request failed (${response.status})`;
    throw new Error(message);
  }


  return responseData;
};
export const forwardAcombaJournalResponse = async ({
  tokenUserId,
  companyId,
  shopId,
  journalEntryId,
  responseData,
}) => {
  const response = await fetch(
    "https://reportapi.beesql.com/api/admin/retailr/report",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token_user_id: tokenUserId,
        company_id: companyId,
        shop_id: shopId,
        journalentry_id: journalEntryId,
        operation_id: "ACOMBA_SDK_JOURNAL_ENTRY_RESPONSE",
        response: responseData,
      }),
    }
  );

  if (!response.ok) {
    const responseText = await response.text().catch(() => "");
    throw new Error(
      responseText || `Acomba response forwarding failed (${response.status})`
    );
  }
};
